import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';

/**
 * Modal for editing contract exit parameters
 * - Days to Exit (countdown timer)
 * - Exit Direction (Gain / Loss)
 * - Exit Percentage
 * - Number of Contracts
 * - Average Cost per Contract
 */
export default function EditTargetModal({ isOpen, onClose, contract, onSave }) {
  const [formData, setFormData] = useState({
    days_to_gain: '',
    target_direction: 'gain',
    target_value: '',
    number_of_contracts: '',
    average_cost_per_contract: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data when modal opens or contract changes
  useEffect(() => {
    if (isOpen && contract) {
      // Parse existing target_percent_change
      let direction = 'gain';
      let value = '';

      if (contract.target_percent_change != null) {
        const targetPercent = parseFloat(contract.target_percent_change);
        direction = targetPercent >= 0 ? 'gain' : 'loss';
        value = Math.abs(targetPercent).toString();
      }

      setFormData({
        days_to_gain: contract.initial_days_to_gain?.toString() || '',
        target_direction: direction,
        target_value: value,
        number_of_contracts: contract.number_of_contracts?.toString() || '1',
        average_cost_per_contract: contract.average_cost_per_contract?.toString() || '',
      });
      setError('');
    }
  }, [isOpen, contract]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear error when user makes changes
  };

  const handleSave = async () => {
    setError('');
    setIsSaving(true);

    try {
      // Build the request payload
      const payload = {};

      // Days to gain (optional)
      if (formData.days_to_gain.trim() !== '') {
        const days = parseInt(formData.days_to_gain);
        if (isNaN(days) || days < 0) {
          setError('Days to Exit must be a positive number');
          setIsSaving(false);
          return;
        }
        payload.days_to_gain = days;
      }

      // Target percent change (optional, but if value is provided, direction is needed)
      if (formData.target_value.trim() !== '') {
        const targetValue = parseFloat(formData.target_value);
        if (isNaN(targetValue) || targetValue < 0) {
          setError('Exit Percentage must be a positive number');
          setIsSaving(false);
          return;
        }

        // Apply direction (negative for loss, positive for gain)
        payload.target_percent_change = formData.target_direction === 'loss'
          ? -targetValue
          : targetValue;
      } else if (formData.target_value.trim() === '') {
        // If target value is cleared, set to null (remove target)
        payload.target_percent_change = null;
      }

      // Number of contracts (optional)
      if (formData.number_of_contracts.trim() !== '') {
        const numContracts = parseInt(formData.number_of_contracts);
        if (isNaN(numContracts) || numContracts < 1) {
          setError('Number of Contracts must be at least 1');
          setIsSaving(false);
          return;
        }
        payload.number_of_contracts = numContracts;
      }

      // Average cost per contract (optional)
      if (formData.average_cost_per_contract.trim() !== '') {
        const avgCost = parseFloat(formData.average_cost_per_contract);
        if (isNaN(avgCost) || avgCost < 0) {
          setError('Average Cost must be a positive number');
          setIsSaving(false);
          return;
        }
        payload.average_cost_per_contract = avgCost;
      }

      // Make API call to update target
      const response = await apiClient.patch(
        `/saved-contracts/${contract.id}/update-target/`,
        payload
      );

      console.log('✅ Target updated successfully:', response.data);

      // Call onSave callback with updated contract data
      if (onSave) {
        onSave(response.data.contract);
      }

      // Close modal
      onClose();
    } catch (err) {
      console.error('❌ Error updating target:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update target';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !contract) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 border border-gray-600 shadow-2xl">
        <h3 className="text-xl font-bold text-white mb-4">
          Edit Exit - {contract.ticker} {contract.option_type?.toUpperCase()} @ ${contract.strike}
        </h3>

        {error && (
          <div className="bg-red-900/30 border border-red-500 text-red-300 px-4 py-2 rounded mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Days to Exit */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Days to Exit <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="number"
              min="0"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 30"
              value={formData.days_to_gain}
              onChange={(e) => handleChange('days_to_gain', e.target.value)}
            />
            <small className="text-xs text-gray-400">
              How many days from now do you plan to sell/exit?
            </small>
          </div>

          {/* Exit Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Exit Percentage <span className="text-gray-500">(optional)</span>
            </label>
            <div className="flex gap-2">
              {/* Direction Dropdown */}
              <select
                className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.target_direction}
                onChange={(e) => handleChange('target_direction', e.target.value)}
              >
                <option value="gain">↑ Gain</option>
                <option value="loss">↓ Loss</option>
              </select>

              {/* Percentage Input */}
              <input
                type="number"
                min="0"
                step="0.1"
                className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 20"
                value={formData.target_value}
                onChange={(e) => handleChange('target_value', e.target.value)}
              />

              <span className="flex items-center text-gray-400 font-medium">%</span>
            </div>
            <small className="text-xs text-gray-400">
              Expected underlying price movement (e.g., 20 for 20% gain or loss)
            </small>
          </div>

          {/* Number of Contracts */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Number of Contracts
            </label>
            <input
              type="number"
              min="1"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 3"
              value={formData.number_of_contracts}
              onChange={(e) => handleChange('number_of_contracts', e.target.value)}
            />
          </div>

          {/* Average Cost per Contract */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Average Cost per Contract
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 39.30"
              value={formData.average_cost_per_contract}
              onChange={(e) => handleChange('average_cost_per_contract', e.target.value)}
            />
            <small className="text-xs text-gray-400">
              Premium paid per contract (used to calculate equity)
            </small>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
